import type { TPhrases } from '@semios/app-platform-i18n-core'
import { translate } from 'i18n/i18n'
import { isEmpty, isNil } from 'lodash'
import { HUB_CONNECTOR_ENVIRONMENT_IS_SWAGWORLD } from 'settings/hubConnectorEnvironment'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import { selectedValueGroupsStore } from 'stores/selectedValueGroupsStore/selectedValueGroupsStore'
import { smallStore } from 'stores/smallStore/smallStore'
import type { TAvailablePreset, TAvailablePresetId } from 'stores/userDetailsStore/helperTypes'
import { userDetailsStore } from 'stores/userDetailsStore/userDetailsStore'
import { apiFetch } from 'utils/apiFetch'
import { isUserOnlyAFreeRegionalUser } from 'utils/isUserOnlyAFreeRegionalUser'
import { setSelectedPreset } from 'utils/setSelectedPreset/setSelectedPreset'
import { showNotification } from 'utils/showNotification'

export const loadDataFromUserAppStartup = async () => {
  const { fieldAssetDataMD5Hash, properties } = fieldAssetStore.getState() ?? {}

  const { user, ...restOfData } = await apiFetch({
    url: '/user-app-startup',
    body: { skipFieldAssetDataIfThisMD5Hash: fieldAssetDataMD5Hash },
    // A cold API instance has taken over a minute here, and a failure replaces the app with the error page
    params: { timeout: 120000 },
  })

  // UserAppStartup API response can sometimes skip properties so we need to check if fieldAssetStore properties are empty
  if (
    ((isEmpty(properties) && isEmpty(restOfData.properties)) || isEmpty(user.permissions)) &&
    user.hasAgreedToCurrentAcceptancePackage
  ) {
    showNotification({
      type: 'error',
      message: translate.phrases.banyanApp(
        'There are no properties or feature attached to your account. Please contact your sales representative for assistance.',
      ),
    })
  }

  let {
    id,
    intercomToken,
    firstName,
    lastName,
    email,
    smsNumber,
    language,
    accountType,
    units: { speed, temperature, pressure, depth, rain, fertigation, volume, flowRate },
    permissions,
    availableSemiosDefinedPresets,
    availableUserDefinedPresets,
    showDataSource,
    showRegionalData,
  } = user

  const shouldWeTryToShowRegionalData = !HUB_CONNECTOR_ENVIRONMENT_IS_SWAGWORLD

  smallStore.setState((s) => ({
    ...s,
    showDataSource: showDataSource ?? true,
    showRegionalData: shouldWeTryToShowRegionalData ? (showRegionalData ?? true) : false,
  }))

  if (permissions['EDIT_SERVICE_CENTER'] && !isEmpty(permissions['EDIT_SERVICE_CENTER'])) {
    if (!availableSemiosDefinedPresets) availableSemiosDefinedPresets = {}

    // TEMPORARY: manually add a preset for Service Center
    availableSemiosDefinedPresets['service-center'] = {
      idHash: 'service-center',
      name: 'Equipment Status',
      ignored: false,
      dayOffsetDateFrom: 0,
      dayOffsetDateTo: 0,
      selectedValueGroups: ['equipment_status'],
      valueGroupForMap: 'equipment_status',
    }
  }

  const localizedAvailableSemiosDefinedPresets = Object.entries(availableSemiosDefinedPresets || {}).reduce<
    Record<TAvailablePresetId, TAvailablePreset>
  >((result, [idHash, semiosDefinedPreset]) => {
    const translatedName = translate.phrases.dbAppPresetsSemiosDefined(
      semiosDefinedPreset.name as TPhrases['dbAppPresetsSemiosDefined'],
    )

    return {
      ...result,
      [idHash]: { ...semiosDefinedPreset, name: translatedName },
    }
  }, {})

  userDetailsStore.setState((s) => {
    return {
      ...s,
      id,
      intercomToken,
      email,
      fertigation: fertigation ?? 'IMPERIAL', // doing this the same way as showDataSource and showRegionalData for now
      depth,
      firstName,
      language,
      accountType,
      lastName,
      pressure,
      rain,
      smsNumber,
      speed,
      volume,
      flowRate,
      temperature,
      permissions,
      availableSemiosDefinedPresets: localizedAvailableSemiosDefinedPresets,
      availableUserDefinedPresets,
      isFreeRegionalUser: user.isFreeRegionalUser,
    }
  })

  fieldAssetStore.setState((s) => ({ ...s, ...restOfData }))

  /**
   * in the event that the user does not have any value groups
   * selected, e.g. upon first booting up the app, we can choose
   * a preset to toggle on for them. We'll go through each of
   * their presets and prefer ones with "weather" in their names
   */
  const selectedValueGroups = selectedValueGroupsStore.getState()?.selectedValueGroups

  if (!Object.values(selectedValueGroups).filter(Boolean).length) {
    let foundPreset: TAvailablePreset['idHash'] | null = null

    const weatherTranslationLowerCase = translate.phrases.dbAppPresetsSemiosDefined('Weather').toLowerCase()

    const weatherAndPestsTranslationLowerCase = translate.phrases
      .dbAppPresetsSemiosDefined('Weather and Pests')
      .toLowerCase()

    const freeRegionalUserPreset = Object.values({
      ...availableSemiosDefinedPresets,
      ...availableUserDefinedPresets,
    }).find((ps) => String(ps.name).toLowerCase().includes(weatherAndPestsTranslationLowerCase))

    if (isUserOnlyAFreeRegionalUser()) {
      foundPreset =
        freeRegionalUserPreset && !freeRegionalUserPreset.ignored ? freeRegionalUserPreset.idHash : null
    } else {
      const presetsToUse = Object.values({
        ...availableSemiosDefinedPresets,
        ...availableUserDefinedPresets,
      }).filter((ps) => ps.idHash !== freeRegionalUserPreset?.idHash)

      presetsToUse
        .sort((a, b) => {
          const aHasWeather = String(a.name).toLowerCase().includes(weatherTranslationLowerCase)
          const bHasWeather = String(b.name).toLowerCase().includes(weatherTranslationLowerCase)

          if (aHasWeather && !bHasWeather) return -1

          if (bHasWeather && !aHasWeather) return 1

          return 0
        })
        .forEach((presetValues) => {
          if (!!foundPreset || !!presetValues.ignored) return

          foundPreset = presetValues.idHash
        })
    }

    if (!isNil(foundPreset)) setSelectedPreset(foundPreset)
  }
}
